import { useState, type FunctionComponent } from "react";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import type { SelectChangeEvent } from "@mui/material/Select";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { useOutletContext } from "react-router-dom";

import { type IncidentReport, type IncidentReportGroupServiceGroup } from "components/CriticalIncident/interfaces";
import { useThrottledSectionUpdate } from '../../hooks/useThrottledSectionUpdate';
import { FormControlWrapper } from "components/Shared";
import { TextBox, Select } from "components/Shared/Inputs";
import { confirm } from "components/Shared/ConfirmDialog";
import { ReportSectionAccordion } from "components/CriticalIncident/components/Shared/ReportSectionAccordion";
import { ChooseYesNo } from "components/CriticalIncident/components/ChooseYesNo";

import "./GroupServices.scss";

const MAX_CHARS = 2000;
const MAX_GROUPS = 10;
const MAX_PARTICIPANTS = 30;

const EMPTY_GROUP: IncidentReportGroupServiceGroup = {
  noOfIndividualParticipants: null,
  wasNatureOfSupportAndConfidentialityDiscussed: null,
  howGroupPresented: '',
  anyInterventionProvided: '',
  anyAdditionalSupportProvided: '',
};

export const GroupServices: FunctionComponent = () => {
  const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
  const { values, handleChange, touched, errors, setFieldValue } = useFormikContext<IncidentReport>();
  const { isReadonly = false } = useOutletContext<{ isReadonly: boolean }>();
  const throttledUpdateReport = useThrottledSectionUpdate('groupService', values.groupService);
  const [expandedGroupIndex, setExpandedGroupIndex] = useState<number | false>(-1);
  const groupServiceGroups = values.groupService?.groups ?? [];

  const handleSessionsChange = async (wereThereGroupSessions: boolean) => {
    if (!wereThereGroupSessions && values.groupService?.wereThereGroupSessions) {
      const confirmed = await confirm({
        title: t('clear_groups_confirm_title'),
        message: t('clear_groups_confirm_text'),
        confirmLabel: t('yes_remove'),
      });
      if (!confirmed) return;
    }

    const groups = wereThereGroupSessions
      ? (groupServiceGroups.length ? groupServiceGroups : [{ ...EMPTY_GROUP }])
      : [];
    const numberOfGroups = wereThereGroupSessions ? (values.groupService?.noOfGroups || 1) : 0;

    setFieldValue('groupService.wereThereGroupSessions', wereThereGroupSessions);
    setFieldValue('groupService.noOfGroups', numberOfGroups);
    setFieldValue('groupService.groups', groups);
    throttledUpdateReport({ wereThereGroupSessions, noOfGroups: numberOfGroups, groups });
  };

  const handleNumberOfGroupsChange = (e: SelectChangeEvent<unknown>) => {
    const numberOfGroups = Number(e.target.value);
    const groupsToAdd = Math.max(0, numberOfGroups - groupServiceGroups.length);
    const groups = [...groupServiceGroups, ...Array.from({ length: groupsToAdd }, () => ({ ...EMPTY_GROUP }))];

    setFieldValue('groupService.noOfGroups', numberOfGroups);
    setFieldValue('groupService.groups', groups);
    throttledUpdateReport({ noOfGroups: numberOfGroups, groups });
  };

  const handleRemoveGroup = (index: number) => {
    const groups = groupServiceGroups.filter((_, i) => i !== index);
    const numberOfGroups = groups.length;

    setFieldValue('groupService.groups', groups);
    setFieldValue('groupService.noOfGroups', numberOfGroups);
    throttledUpdateReport({ groups, noOfGroups: numberOfGroups });
  };

  const updateGroupField = (index: number, field: keyof IncidentReportGroupServiceGroup, value: unknown) => {
    const groups = groupServiceGroups.map((group, i) => i === index ? { ...group, [field]: value } : group);
    throttledUpdateReport({ groups });
  };

  const makeGroupChangeHandler = (index: number, field: keyof IncidentReportGroupServiceGroup) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleChange(event);
      updateGroupField(index, field, event.target.value);
    };

  const handleGroupBooleanChange = (index: number, field: keyof IncidentReportGroupServiceGroup) => (boolValue: boolean | null) => {
    setFieldValue(`groupService.groups[${index}].${field}`, boolValue);
    updateGroupField(index, field, boolValue);
  };

  return translationsReady && (
    <div className="group-services">
      <h2>{t('groupService')}</h2>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            type="radio.yesno"
            label={`${t('were_there_group_sessions')}*`}
            name="groupService.wereThereGroupSessions"
            fullWidth
            error={touched.groupService?.wereThereGroupSessions && Boolean(errors?.groupService?.wereThereGroupSessions)}
            helperText={touched.groupService?.wereThereGroupSessions ? t(errors?.groupService?.wereThereGroupSessions || ' ') : ''}
          >
            <ChooseYesNo
              label={`${t('were_there_group_sessions')}*`}
              name="groupService.wereThereGroupSessions"
              value={values.groupService?.wereThereGroupSessions}
              onChange={(value) => handleSessionsChange(value as boolean)}
            />
          </FormControlWrapper>
        </Grid>

        {values.groupService?.wereThereGroupSessions && (
          <>
            <Grid item sm={12}>
              <FormControlWrapper
                isReadonly={isReadonly}
                type="select"
                fullWidth
                label={`${t('number_of_groups')}*`}
                name="groupService.noOfGroups"
                error={touched.groupService?.noOfGroups && Boolean(errors?.groupService?.noOfGroups)}
                helperText={touched.groupService?.noOfGroups && t(errors?.groupService?.noOfGroups || ' ')}
                options={Array.from({ length: MAX_GROUPS }, (_, i) => ({ label: `${i + 1}`, value: `${i + 1}` }))}
              >
                <Select
                  name="groupService.noOfGroups"
                  value={values.groupService?.noOfGroups}
                  onChange={handleNumberOfGroupsChange}
                >
                  {Array.from({ length: MAX_GROUPS }, (_, i) => i + 1).map(n => (
                    <MenuItem value={n} key={n} disabled={n <= groupServiceGroups.length && n !== values.groupService?.noOfGroups}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControlWrapper>
            </Grid>

            {groupServiceGroups.map((group, index) => (
              <Grid item sm={12} key={index}>
                <ReportSectionAccordion
                  label={t('group_number', { number: index + 1 })}
                  name={`groupService.groups[${index}]`}
                  pristineValue={EMPTY_GROUP}
                  removeAction={isReadonly ? undefined : () => handleRemoveGroup(index)}
                  expanded={expandedGroupIndex === index}
                  onToggle={(isExpanded) => setExpandedGroupIndex(isExpanded ? index : false)}
                  readonly={isReadonly}
                  removeHidden={groupServiceGroups.length <= 1}
                >
                  <Grid container spacing={2}>
                    <Grid item sm={12}>
                      <FormControlWrapper
                        isReadonly={isReadonly}
                        label={t('number_of_individual_participants')}
                        name={`groupService.groups[${index}].noOfIndividualParticipants`}
                        fullWidth
                        className="oneline_text_box"
                      >
                        <TextBox
                          type="positive-integer"
                          label={t('number_of_individual_participants')}
                          required
                          name={`groupService.groups[${index}].noOfIndividualParticipants`}
                          value={group.noOfIndividualParticipants}
                          onChange={makeGroupChangeHandler(index, 'noOfIndividualParticipants')}
                          slotProps={{
                            input: { min: 1, max: MAX_PARTICIPANTS }
                          }}
                          error={Boolean((touched.groupService?.groups?.[index] as any)?.noOfIndividualParticipants) && Boolean((errors?.groupService?.groups?.[index] as any)?.noOfIndividualParticipants)}
                          helperText={(touched.groupService?.groups?.[index] as any)?.noOfIndividualParticipants ? t((errors?.groupService?.groups?.[index] as any)?.noOfIndividualParticipants || ' ') : ''}
                        />
                      </FormControlWrapper>
                    </Grid>
                    <Grid item sm={12}>
                      <FormControlWrapper
                        isReadonly={isReadonly}
                        type="radio.yesno"
                        label={`${t('was_telus_health_support_discussed')}*`}
                        name={`groupService.groups[${index}].wasNatureOfSupportAndConfidentialityDiscussed`}
                        fullWidth
                        error={Boolean((touched.groupService?.groups?.[index] as any)?.wasNatureOfSupportAndConfidentialityDiscussed) && Boolean((errors?.groupService?.groups?.[index] as any)?.wasNatureOfSupportAndConfidentialityDiscussed)}
                        helperText={(touched.groupService?.groups?.[index] as any)?.wasNatureOfSupportAndConfidentialityDiscussed ? t((errors?.groupService?.groups?.[index] as any)?.wasNatureOfSupportAndConfidentialityDiscussed || ' ') : ''}
                      >
                        <ChooseYesNo
                          label={`${t('was_telus_health_support_discussed')}*`}
                          name={`groupService.groups[${index}].wasNatureOfSupportAndConfidentialityDiscussed`}
                          value={group.wasNatureOfSupportAndConfidentialityDiscussed}
                          onChange={handleGroupBooleanChange(index, 'wasNatureOfSupportAndConfidentialityDiscussed')}
                        />
                      </FormControlWrapper>
                    </Grid>
                    <Grid item sm={12}>
                      <FormControlWrapper
                        isReadonly={isReadonly}
                        label={t('how_did_individuals_present')}
                        name={`groupService.groups[${index}].howGroupPresented`}
                        fullWidth
                      >
                        <TextBox
                          multiline
                          minRows={4}
                          maxRows={6}
                          label={t('how_did_individuals_present')}
                          name={`groupService.groups[${index}].howGroupPresented`}
                          value={group.howGroupPresented}
                          onChange={makeGroupChangeHandler(index, 'howGroupPresented')}
                          slotProps={{
                            input: { maxLength: MAX_CHARS }
                          }}
                        />
                      </FormControlWrapper>
                    </Grid>
                    <Grid item sm={12}>
                      <FormControlWrapper
                        isReadonly={isReadonly}
                        label={t('which_intervention_was_provided')}
                        name={`groupService.groups[${index}].anyInterventionProvided`}
                        fullWidth
                      >
                        <TextBox
                          multiline
                          minRows={4}
                          maxRows={6}
                          label={t('which_intervention_was_provided')}
                          required
                          name={`groupService.groups[${index}].anyInterventionProvided`}
                          value={group.anyInterventionProvided}
                          onChange={makeGroupChangeHandler(index, 'anyInterventionProvided')}
                          slotProps={{
                            input: { maxLength: MAX_CHARS }
                          }}
                          error={Boolean((touched.groupService?.groups?.[index] as any)?.anyInterventionProvided) && Boolean((errors?.groupService?.groups?.[index] as any)?.anyInterventionProvided)}
                          helperText={(touched.groupService?.groups?.[index] as any)?.anyInterventionProvided ? t((errors?.groupService?.groups?.[index] as any)?.anyInterventionProvided || ' ') : ''}
                        />
                      </FormControlWrapper>
                    </Grid>
                    <Grid item sm={12}>
                      <FormControlWrapper
                        isReadonly={isReadonly}
                        label={t('what_additional_support_was_provided')}
                        name={`groupService.groups[${index}].anyAdditionalSupportProvided`}
                        fullWidth
                      >
                        <TextBox
                          multiline
                          minRows={4}
                          maxRows={6}
                          label={t('what_additional_support_was_provided')}
                          name={`groupService.groups[${index}].anyAdditionalSupportProvided`}
                          value={group.anyAdditionalSupportProvided}
                          onChange={makeGroupChangeHandler(index, 'anyAdditionalSupportProvided')}
                          slotProps={{
                            input: { maxLength: MAX_CHARS }
                          }}
                        />
                      </FormControlWrapper>
                    </Grid>
                  </Grid>
                </ReportSectionAccordion>
              </Grid>
            ))}
          </>
        )}
      </Grid>
    </div>
  );
};
